import { formatRoleLabel } from "@/components/common";

// Fleet fixtures (Codex OAuth live-test tenants, delete canaries, sandboxes)
// are granted to every internal identity by the automatic domain policy, so
// they show up next to real brands. Matching is boundary-aware so brand names
// like "Contest Co" or "Testosterone Labs" stay classified as brands.
const TEST_WORKSPACE_PATTERN = /(^|[\s_-])(test|tests|testing|canary|sandbox|synthetic)([\s_-]|$)/i;
const FIXTURE_SLUG_PREFIXES = ["codex-oauth-live-test-", "admin-delete-"];

export function clientInitials(clientName) {
  const normalized = String(clientName ?? "").trim();
  if (!normalized) return "W";
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts.map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

export function classifyWorkspace({ clientSlug, clientName } = {}) {
  const slug = String(clientSlug ?? "").trim();
  if (FIXTURE_SLUG_PREFIXES.some((prefix) => slug.startsWith(prefix))) return "test";
  return TEST_WORKSPACE_PATTERN.test(`${slug} ${String(clientName ?? "")}`) ? "test" : "brand";
}

// The agent's picture the server sent for a workspace (HUB #337), only when there is one.
function pictureOf(membership) {
  const agentImageUrl = membership?.agentImageUrl;
  return typeof agentImageUrl === "string" && agentImageUrl ? { agentImageUrl } : {};
}

export function buildWorkspaceOptions(memberships = [], activeClient = null) {
  const unique = new Map();
  for (const membership of memberships) {
    const clientSlug = String(membership?.clientSlug ?? "").trim();
    const clientName = String(membership?.clientName ?? "").trim();
    if (!clientSlug || !clientName || unique.has(clientSlug)) continue;
    unique.set(clientSlug, {
      clientSlug,
      clientName,
      initials: clientInitials(clientName),
      roleLabel: formatRoleLabel(membership.role),
      kind: classifyWorkspace({ clientSlug, clientName }),
      active: clientSlug === activeClient?.clientSlug,
      ...pictureOf(membership),
    });
  }

  const nameCounts = new Map();
  for (const option of unique.values()) {
    const key = option.clientName.toLowerCase();
    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
  }

  return [...unique.values()]
    .map((option) => ({ ...option, showSlug: nameCounts.get(option.clientName.toLowerCase()) > 1 }))
    .sort((left, right) => {
      if (left.active !== right.active) return left.active ? -1 : 1;
      if (left.kind !== right.kind) return left.kind === "brand" ? -1 : 1;
      return left.clientName.localeCompare(right.clientName, undefined, { sensitivity: "base" })
        || left.clientSlug.localeCompare(right.clientSlug);
    });
}

export function matchesWorkspaceQuery(option, normalizedQuery) {
  if (!normalizedQuery) return true;
  return option.clientName.toLowerCase().includes(normalizedQuery) || option.clientSlug.includes(normalizedQuery);
}
